import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { getCabVehicle, listCabLocations, requestCabQuote } from '../../api/cabsPublic';
import { getCategoryFallbackImage } from './helpers';
import CabsBreadcrumbs from './CabsBreadcrumbs';

const CabsDetail = () => {
  const { vehicleId } = useParams();
  const location = useLocation();
  const [vehicle, setVehicle] = useState(null);
  const [locations, setLocations] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estimating, setEstimating] = useState(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const tripTypeParam = searchParams.get('tripType');
  const tripType = tripTypeParam === 'roundtrip' ? 'ROUNDTRIP' : 'ONEWAY';
  const pickupLocationId = searchParams.get('pickupLocationId') || '';
  const dropLocationId = searchParams.get('dropLocationId') || '';
  const pickupDate = searchParams.get('pickupDate') || '';
  const pickupTime = searchParams.get('pickupTime') || '';
  const returnDate = searchParams.get('returnDate') || '';
  const passengers = searchParams.get('passengers') || 1;

  useEffect(() => {
    listCabLocations().then((res) => setLocations(res.data || res || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getCabVehicle(vehicleId);
        if (!cancelled) setVehicle(res.data || res || null);
      } catch {
        if (!cancelled) setVehicle(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [vehicleId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!vehicle || !pickupLocationId || !dropLocationId) {
        setEstimate(null);
        return;
      }
      setEstimating(true);
      try {
        const result = await requestCabQuote({
          tripType,
          pickupLocationId,
          dropLocationId,
          passengers: Number(passengers || 1),
          pickupAt: pickupDate ? `${pickupDate}${pickupTime ? `T${pickupTime}` : 'T09:00'}` : undefined,
          returnAt: returnDate ? `${returnDate}T09:00` : undefined,
          vehicleCategoryId: vehicle.categoryId,
          seatConfigId: vehicle.seatConfigId,
        });
        if (!cancelled) setEstimate(result.data || result || null);
      } catch {
        if (!cancelled) setEstimate(null);
      } finally {
        if (!cancelled) setEstimating(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [vehicle, tripType, pickupLocationId, dropLocationId, pickupDate, pickupTime, returnDate, passengers]);

  const locationMap = useMemo(() => {
    const map = {};
    locations.forEach((loc) => {
      map[loc.id] = loc;
    });
    return map;
  }, [locations]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
        Loading cab details...
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
        Vehicle not found.
      </div>
    );
  }

  const cover = vehicle.images?.find((img) => img.isCover) || vehicle.images?.[0];
  const fallback = getCategoryFallbackImage(vehicle.category?.name);
  const coverUrl = cover?.url || vehicle.category?.imageUrl || fallback;
  const pickupLabel = pickupLocationId ? locationMap[pickupLocationId]?.name || 'Pickup' : 'Pickup';
  const dropLabel = dropLocationId ? locationMap[dropLocationId]?.name || 'Drop' : 'Drop';

  return (
    <section className="space-y-6">
      <CabsBreadcrumbs
        items={[
          { label: 'Home', to: '/' },
          { label: 'Cabs', to: '/cabs' },
          { label: 'Listing', to: '/cabs/list' },
          { label: vehicle.name },
        ]}
      />

      <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="overflow-hidden rounded-xl bg-slate-100">
            {coverUrl ? (
              <img src={coverUrl} alt={cover?.altText || vehicle.name} className="h-64 w-full object-cover" />
            ) : (
              <div className="flex h-64 items-center justify-center text-xs text-slate-400">No image</div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">{vehicle.name}</h1>
              <p className="text-sm text-slate-500">
                {vehicle.category?.name || 'Category'} - {vehicle.seatConfig?.seatCapacity || '--'} seats
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-slate-400">Estimated fare</p>
              <p className="text-2xl font-semibold text-slate-900">
                {estimate?.total ? `INR ${estimate.total}` : estimating ? 'Calculating...' : 'Unavailable'}
              </p>
              {estimate?.distanceKm ? <p className="text-xs text-slate-500">{estimate.distanceKm} km</p> : null}
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600">
              <p className="font-semibold text-slate-900">Trip summary</p>
              <p className="mt-2">{tripType === 'ROUNDTRIP' ? 'Round trip' : 'One way'} - {pickupLabel} to {dropLabel}</p>
              <p>Pickup: {pickupDate || 'Not set'} {pickupTime || ''}</p>
              {tripType === 'ROUNDTRIP' && <p>Return: {returnDate || 'Not set'}</p>}
              <p>Passengers: {passengers}</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600">
              <p className="font-semibold text-slate-900">Amenities</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {(vehicle.amenities || []).map((amenity) => (
                  <span key={amenity.id} className="rounded-full bg-white px-3 py-1 text-xs text-slate-600">
                    {amenity.amenity?.name || amenity.name}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {estimate?.breakdown?.length ? (
            <div className="mt-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Fare breakdown</p>
              <div className="mt-2 space-y-2 text-sm text-slate-600">
                {estimate.breakdown.map((item) => (
                  <div key={item.key} className="flex items-center justify-between">
                    <span>{item.label}</span>
                    <span>INR {item.amount}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600">
              <p className="font-semibold text-slate-900">Overview</p>
              <p className="mt-2">Brand: {vehicle.brand || 'N/A'}</p>
              <p>Model: {vehicle.model || 'N/A'}</p>
              <p>Transmission: {vehicle.transmission || 'N/A'}</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600">
              <p className="font-semibold text-slate-900">Policies</p>
              <ul className="mt-2 list-disc pl-4">
                <li>Driver details shared before pickup</li>
                <li>Free cancellation up to 24 hours</li>
                <li>Toll and parking as applicable</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Next step</p>
            <h3 className="mt-2 text-lg font-semibold text-slate-900">Confirm your outstation request</h3>
            <p className="mt-2 text-sm text-slate-600">
              This vehicle is available for your selected route. Continue to enquiry or adjust trip details.
            </p>
            {estimate?.total ? (
              <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-xs text-emerald-800">
                Estimated total: <strong>INR {estimate.total}</strong>
              </div>
            ) : null}
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to={`/cabs/list${location.search}`}
                className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600"
              >
                Back to listing
              </Link>
              <Link
                to={`/cabs/${vehicle.id}/enquiry${location.search}`}
                className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white"
              >
                Send enquiry
              </Link>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-600 shadow-sm">
            Estimates are calculated using current pricing rules and route distance.
          </div>
        </div>
      </div>
    </section>
  );
};

export default CabsDetail;
